import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `GRP-JOIN-REQUEST` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/join-requests` (ADR-0009 bất biến 5).
 * Cửa mobile trang này chịu trách nhiệm (QĐ PO 17/09 mục 12 — xin vào nhóm / huỷ xin, tạm redirect).
 *
 * 🔴 Vai HẸP LẠI còn `OWNER_ADMIN` (US-AIV2-0049 vòng 2, review #827 HIGH-2 — ĐỪNG nới lại):
 *   `ai-capability-gate.service.ts` lấy CHÍNH `page.roles` làm cổng (`page.roles.includes(seat.role)`).
 *   Khai `ALL_ROLES` ⇒ member/thủ quỹ QUA được cổng chat, rồi mở màn `/groups/:groupId/join-requests`
 *   (danh sách QUẢN TRỊ) và 403 ở bước chốt — đúng thứ CLAUDE.md §4 cấm.
 *   Câu chào của trang (`"Mình mở danh sách người đang chờ vào nhóm nhé."`) cũng chỉ mô tả trải nghiệm
 *   QUẢN TRỊ, nên vai rộng là một lời hứa mà màn hình không giữ.
 *
 * ⚠️ Lưu ý cho người đọc sau — đây KHÔNG phải ca mà `roles-parity` bắt được, và cũng không nên trông chờ nó bắt:
 *   trang còn giữ cửa tự-phục-vụ `DELETE /users/me/groups/{groupId}/join-request` (không guard vai), nên theo
 *   luật "⊆ HỢP các cửa" thì `ALL_ROLES` vẫn HỢP LỆ — không vai nào vào ngõ cụt về mặt CỬA. Cái sai nằm ở
 *   trục khác: **màn hình** của trang chỉ phục vụ quản trị. Giữ cửa đó ở đây là để bản đồ còn KÍN (mỗi cửa
 *   đúng một chủ), không phải để mời member vào.
 *
 * ✅ HỆ QUẢ ĐÃ ĐÓNG (QĐ điều phối 17/09, vòng 3): việc *"huỷ xin vào nhóm của chính mình"* đã tách sang trang
 *   riêng **`JOIN-REQUEST-MINE`** (vai: mọi member; giữ cửa `DELETE /users/me/groups/{groupId}/join-request`).
 *   Hai câu ví dụ `huỷ xin vào nhóm` cũng chuyển sang đó — để chúng ở lại đây sẽ dẫn member tới 403 ở CỔNG.
 */
export const GRP_JOIN_REQUEST = {
  id: "GRP-JOIN-REQUEST",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: [],
  slots: [],
  llm: {
    description:
      "Duyệt hoặc TỪ CHỐI ĐƠN của người NGOÀI đang xin vào nhóm — việc của người quản trị. Ở đây 'duyệt'/'từ chối' là quyết về NGƯỜI xin vào, KHÔNG phải về KHOẢN CHI (duyệt/từ chối khoản chi là EXP-APPROVE). KHÔNG phải lời mời gửi cho chính tôi (đó là INVITE-ACCEPT), cũng KHÔNG phải rút lại đơn xin của chính mình (đó là JOIN-REQUEST-MINE).",
    examples: [
      "ai đang xin vào nhóm",
      "ai dang xin vao nhom",
      "duyệt người xin vào",
      "duyet nguoi xin vao",
      "từ chối người xin vào",
      "tu choi nguoi xin vao",
      // ——— 0105: thêm cho đủ 40 ———
      "danh sách người chờ duyệt vào nhóm",
      "danh sach nguoi cho duyet vao nhom",
      "có ai xin vào nhóm không",
      "co ai xin vao nhom khong",
      "duyệt cho người đang chờ đó vào nhóm",
      "duyet cho nguoi dang cho do vao nhom",
      "duyệt đơn xin vào nhóm",
      "duyet don xin vao nhom",
      "xem yêu cầu tham gia nhóm",
      "xem yeu cau tham gia nhom",
      "chấp nhận người xin vào nhóm",
      "chap nhan nguoi xin vao nhom",
      "ai đang chờ mình duyệt vào nhóm",
      "ai dang cho minh duyet vao nhom",
      "duyệt hết mấy người đang chờ vào nhóm",
      "duyet het may nguoi dang cho vao nhom",
      "bỏ qua yêu cầu xin vào nhóm",
      "bo qua yeu cau xin vao nhom",
      "người lạ xin vào nhóm, xử lý sao",
      "nguoi la xin vao nhom xu ly sao",
      "đơn xin vào nhóm",
      "don xin vao nhom",
      "duyệt thành viên mới xin vào",
      "duyet thanh vien moi xin vao",
      "từ chối đơn tham gia nhóm",
      "tu choi don tham gia nhom",
      "yêu cầu vào nhóm đang chờ",
      "yeu cau vao nhom dang cho",
      "mở danh sách chờ duyệt vào nhóm",
      "mo danh sach cho duyet vao nhom",
      "không cho người đó vào nhóm",
      "khong cho nguoi do vao nhom",
      "duyệt cho phụ huynh mới xin vào nhóm lớp",
      "duyet cho phu huynh moi xin vao nhom lop",
    ],
  },
  screen: "/groups/:groupId/join-requests",
  screenShows: "join_requests",
  endpoints: [
    "POST /groups/{groupId}/members/approve",
    "POST /groups/{groupId}/members/reject",
    // QĐ người dùng 27/10/2026 (@UC9516) — lời xin làm người đại diện thứ 2+ của một suất: cùng mục chờ duyệt, cùng vai O/A.
    "GET /groups/{groupId}/representative-requests",
    "POST /groups/{groupId}/representative-requests/approve",
    "POST /groups/{groupId}/representative-requests/reject",
  ],
  facts: [],
  templates: {
    redirect: "Mình mở danh sách người đang chờ vào nhóm nhé.",
  },
  journey: { after: ["READ-PEOPLE-MINE"] },
  help: { title: "Người xin vào nhóm", tryNow: "ai đang xin vào nhóm" },
} satisfies AiCapabilityPage;
